/**
 * The Ledger Skeleton: a surface-2 block shaped like the content it stands
 * for, shimmering to surface-3 once per 1.6s (the `skeleton` recipe). It never
 * shows before 150ms of loading, so a fast load does not flash: pass `loading`
 * and the wrapper waits through `useDelayedFlag`. Before the delay the block
 * holds its space unseen; after it, it is announced as loading.
 *
 * Spec: specs/design-system/ledger-primitives.feature
 */
import {
  Skeleton as ChakraSkeleton,
  type SkeletonProps as ChakraSkeletonProps,
} from "@chakra-ui/react";
import * as React from "react";
import { useDelayedFlag } from "~/hooks/useDelayedFlag";

export interface SkeletonProps extends ChakraSkeletonProps {
  /** Show the skeleton while true (after the delay); the children otherwise. */
  loading?: boolean;
  /** How long loading must last before the skeleton appears. */
  delayMs?: number;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton({ loading = true, delayMs = 150, children, ...rest }, ref) {
    const show = useDelayedFlag({ flag: loading, delayMs });
    if (!loading) return <>{children}</>;
    return (
      <ChakraSkeleton
        ref={ref}
        loading
        // Before the delay the block holds its space, unseen, so nothing
        // jumps when it appears.
        visibility={show ? undefined : "hidden"}
        aria-busy="true"
        aria-label="Loading"
        role="status"
        {...rest}
      >
        {children}
      </ChakraSkeleton>
    );
  },
);
